/**
 * Composer - Custom composer for the assistant that integrates
 * skills/tools UI with Assistant-UI's runtime.
 *
 * Always uses MentionInput for text entry. When skills are enabled,
 * registers a <Mention> child for slash command support.
 *
 * The assistant-ui composer is the single source of truth for text.
 * Draft persistence is handled by useDraftSync, which observes
 * composer.text and debounce-saves to localStorage as a side-effect.
 */
import { useEffect, useRef, useState } from "react";
import { ComposerPrimitive, useAui, useAuiState } from "@assistant-ui/react";
import { ArrowUpIcon, SquareIcon } from "lucide-react";
import { ContextBufferMeter } from "~/components/assistant-v2/ContextBufferMeter";
import { TooltipIconButton } from "~/components/assistant-v2/TooltipIconButton";
import type { ContextBuffer } from "~/components/assistant-v2/useContextBuffer";
import { useDraftSync } from "~/components/assistant-v2/useDraftSync";
import { MentionInput, Mention, type MentionInputRef } from "~/components/assistant/MentionInput";
import SkillEditorModal, {
  type SkillFormData,
  type SkillFormErrors,
  type SkillTag,
} from "~/components/assistant/SkillEditorModal";
import { SkillSelector } from "~/components/assistant/SkillSelector";
import { SkillsPopup } from "~/components/assistant/SkillsPopup";
import { ToolSelector } from "~/components/assistant/ToolSelector";
import { ZENDESK_SKILLS_URL } from "~/components/assistant/constants";
import type {
  MCPServer,
  McpGuidelines,
  PartialSkill,
  Skill,
  SkillCreate,
  SkillUpdate,
} from "~/components/assistant/types";
import { cn } from "~/lib/utils";

export interface ComposerProps {
  contextId: string;
  conversationId: string | null;
  skillsEnabled?: boolean;
  systemSkills?: PartialSkill[];
  userSkills?: PartialSkill[];
  datasetSkills?: PartialSkill[];
  isLoadingSkills?: boolean;
  onCreateSkill?: (skill: SkillCreate) => Promise<void>;
  onUpdateSkill?: (skillId: string, skill: SkillUpdate) => Promise<void>;
  onDeleteSkill?: (skillId: string) => Promise<void>;
  onGetSkill?: (skillId: string) => Promise<Skill>;
  mcpServers?: MCPServer[];
  selectedMcpServers?: string[];
  onToggleMcpServer?: (serverId: string) => void;
  showToolSelector?: boolean;
  placeholder?: string;
  contextBuffer?: ContextBuffer | null;
  /** True when the agent is running on a remote replica (detected via status polling) */
  isRunningRemotely?: boolean;
  /** Cancel a running agent (aborts SSE + notifies backend). */
  onCancelRun?: () => void;
  /** True while waiting for backend to acknowledge cancellation */
  isCancelling?: boolean;
  mcpGuidelines?: McpGuidelines;
}

/**
 * Registers a ModelContextProvider that injects selected MCP server IDs
 * into the AG-UI request's forwardedProps. The AG-UI runtime spreads
 * ModelContext.config into forwardedProps in buildRunInput().
 *
 * Must be called inside AssistantRuntimeProvider.
 */
function useForwardMcpServers(selectedMcpServers: string[], mcpServers: MCPServer[]) {
  const aui = useAui();

  useEffect(() => {
    return aui.modelContext().register({
      getModelContext: () => {
        const serversToSend = selectedMcpServers.filter((serverId) => {
          const server = mcpServers.find((s) => s.id === serverId);
          return server && !server.ui_only;
        });
        return {
          config: { servers: serversToSend } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
        };
      },
    });
  }, [aui, selectedMcpServers, mcpServers]);
}

export function Composer({
  contextId,
  conversationId,
  skillsEnabled = false,
  systemSkills = [],
  userSkills = [],
  datasetSkills = [],
  isLoadingSkills = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  onCreateSkill,
  onUpdateSkill,
  onDeleteSkill,
  onGetSkill,
  mcpServers = [],
  selectedMcpServers = [],
  onToggleMcpServer,
  showToolSelector = true,
  placeholder = "Send a message...",
  contextBuffer,
  isRunningRemotely = false,
  onCancelRun,
  isCancelling = false,
  mcpGuidelines,
}: ComposerProps) {
  useForwardMcpServers(selectedMcpServers, mcpServers);

  const aui = useAui();
  const composer = aui.composer();
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const composerText = useAuiState((s) => s.composer.text);

  const mentionInputRef = useRef<MentionInputRef>(null);
  const { clearDraft } = useDraftSync({ contextId, conversationId });

  // Skill editor state
  const [skillEditorOpen, setSkillEditorOpen] = useState(false);
  const [skillEditorErrors, setSkillEditorErrors] = useState<SkillFormErrors>({});
  const [savingSkill, setSavingSkill] = useState(false);
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
  const [editingSkillData, setEditingSkillData] = useState<Partial<SkillFormData> | undefined>(
    undefined
  );
  const [loadingSkillContent, setLoadingSkillContent] = useState(false);

  const handleInputChange = (value: string) => {
    composer.setText(value);
  };

  const handleSend = () => {
    if (!composerText.trim() || isRunning || isRunningRemotely) {
      return;
    }
    composer.send();
    clearDraft();
  };

  const handleCancel = () => {
    onCancelRun?.();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Skill CRUD handlers
  const handleAddSkill = () => {
    setEditingSkillId(null);
    setEditingSkillData(undefined);
    setSkillEditorOpen(true);
    setSkillEditorErrors({});
  };

  const handleEditSkill = async (skillId: string) => {
    if (!onGetSkill) {
      return;
    }

    setEditingSkillId(skillId);
    setEditingSkillData(undefined);
    setSkillEditorOpen(true);
    setSkillEditorErrors({});
    setLoadingSkillContent(true);

    try {
      const skill = await onGetSkill(skillId);
      setEditingSkillData({
        name: skill.name,
        description: skill.description ?? "",
        tags: (skill.tags ?? []) as SkillTag[],
        content: skill.content,
      });
    } catch (error) {
      console.error("Failed to load skill:", error);
      setSkillEditorErrors({ general: "Failed to load skill content" });
    } finally {
      setLoadingSkillContent(false);
    }
  };

  const handleDeleteSkill = async (skillId: string) => {
    if (!onDeleteSkill) {
      return;
    }
    try {
      await onDeleteSkill(skillId);
    } catch (error) {
      console.error("Failed to delete skill:", error);
    }
  };

  const handleSaveSkill = async (data: SkillFormData) => {
    if (!onCreateSkill || !onUpdateSkill) {
      return;
    }

    setSavingSkill(true);
    setSkillEditorErrors({});

    try {
      const payload = {
        name: data.name,
        description: data.description || undefined,
        content: data.content,
        tags: data.tags.length > 0 ? data.tags : undefined,
      };

      if (editingSkillId) {
        await onUpdateSkill(editingSkillId, payload);
      } else {
        await onCreateSkill({ ...payload, scope: "USER" });
      }

      setSkillEditorOpen(false);
      setEditingSkillId(null);
      setEditingSkillData(undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save skill";
      setSkillEditorErrors({ general: message });
    } finally {
      setSavingSkill(false);
    }
  };

  const hasFeatures = showToolSelector || skillsEnabled;

  return (
    <>
      <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col">
        <div className="flex w-full flex-col rounded-2xl border border-input bg-background px-1 pt-2 outline-none transition-all duration-200 hover:ring-1 hover:ring-gray-300 focus-within:ring-1 focus-within:ring-gray-300">
          <div className="mb-1 px-3 pt-2 pb-3">
            <MentionInput
              ref={mentionInputRef}
              value={composerText}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              placeholder={placeholder}
              maxHeight={128}
              autoFocus
            >
              {skillsEnabled && (
                <Mention
                  trigger="/"
                  type="skill"
                  pattern={/\/[\w-]+/g}
                  className="text-block rounded px-1.5 py-0 inline-block mx-0.5 my-0.25"
                  data={[...systemSkills, ...datasetSkills, ...userSkills]}
                  displayTransform={(skill: PartialSkill) => skill.name}
                  renderSuggestions={(props) => (
                    <div
                      style={{
                        position: "fixed",
                        top: props.position.top - 8,
                        left: props.position.left,
                        transform: "translateY(-100%)",
                        zIndex: 50,
                      }}
                      className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(84,72,49,0.08),0_3px_8px_rgba(0,0,0,0.12)] overflow-hidden w-84"
                    >
                      <SkillsPopup
                        systemSkills={systemSkills}
                        userSkills={userSkills}
                        datasetSkills={datasetSkills}
                        searchFilter={props.searchText}
                        onSelectSkill={(name) => props.onSelect(`/${name}`)}
                        onAddSkill={() => {
                          handleAddSkill();
                          props.onClose();
                        }}
                        onEditSkill={(id) => {
                          handleEditSkill(id);
                          props.onClose();
                        }}
                        onDeleteSkill={handleDeleteSkill}
                        onLearnMore={() =>
                          window.open(ZENDESK_SKILLS_URL, "_blank", "noopener,noreferrer")
                        }
                        enableKeyboardNav={true}
                        onClose={props.onClose}
                      />
                    </div>
                  )}
                />
              )}
            </MentionInput>
          </div>

          <div className="relative mx-2 mb-2 flex items-center justify-between">
            {/* Left: Tools and skills selectors */}
            <div className="flex items-center gap-0.5">
              {showToolSelector && onToggleMcpServer && (
                <ToolSelector
                  servers={mcpServers}
                  selectedServerIds={selectedMcpServers}
                  onToggle={onToggleMcpServer}
                  mcpGuidelines={mcpGuidelines}
                />
              )}
              {skillsEnabled && (
                <>
                  <SkillSelector
                    systemSkills={systemSkills}
                    userSkills={userSkills}
                    datasetSkills={datasetSkills}
                    onSelectSkill={(name) => {
                      composer.setText(composerText + `/${name} `);
                      setTimeout(() => {
                        mentionInputRef.current?.focusAtEnd();
                      }, 0);
                    }}
                    onAddSkill={handleAddSkill}
                    onEditSkill={handleEditSkill}
                    onDeleteSkill={handleDeleteSkill}
                  />
                  <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                    V2
                  </span>
                </>
              )}
              {!hasFeatures && <div />}
            </div>

            {/* Right: Context meter + Send/Cancel */}
            <div className="flex items-center gap-3">
              {contextBuffer && <ContextBufferMeter buffer={contextBuffer} />}
              <ComposerAction
                isRunning={isRunning || isRunningRemotely}
                isCancelling={isCancelling}
                onSend={handleSend}
                onCancel={handleCancel}
                canSend={composerText.trim().length > 0}
              />
            </div>
          </div>
        </div>
      </ComposerPrimitive.Root>

      {/* Skill modals */}
      {skillsEnabled && (
        <>
          {skillEditorOpen && (
            <SkillEditorModal
              key={editingSkillId ?? "new"}
              open={skillEditorOpen}
              initialData={editingSkillData}
              onClose={() => {
                setSkillEditorOpen(false);
                setEditingSkillId(null);
                setEditingSkillData(undefined);
                setSkillEditorErrors({});
              }}
              onSubmit={handleSaveSkill}
              loading={savingSkill || loadingSkillContent}
              errors={skillEditorErrors}
              isEditing={!!editingSkillId}
            />
          )}
        </>
      )}
    </>
  );
}

function ComposerAction({
  isRunning,
  isCancelling,
  onSend,
  onCancel,
  canSend,
}: {
  isRunning: boolean;
  isCancelling: boolean;
  onSend: () => void;
  onCancel: () => void;
  canSend: boolean;
}) {
  const showStop = isRunning || isCancelling;

  if (showStop) {
    return (
      <TooltipIconButton
        tooltip="Stop agent"
        side="bottom"
        type="button"
        variant="default"
        size="icon"
        className={cn("size-7 rounded-full", isCancelling && "bg-gray-400")}
        aria-label="Stop agent"
        onClick={onCancel}
        disabled={isCancelling}
      >
        <SquareIcon className="size-3 fill-current" />
      </TooltipIconButton>
    );
  }

  return (
    <TooltipIconButton
      tooltip="Send message"
      side="bottom"
      type="button"
      variant="default"
      size="icon"
      className="size-7 rounded-full bg-primary hover:bg-primary/80 disabled:bg-gray-400"
      aria-label="Send message"
      onClick={onSend}
      disabled={!canSend}
    >
      <ArrowUpIcon className="size-4" />
    </TooltipIconButton>
  );
}
